import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Inbox, UserCheck, UserX, Mail, Send, Settings, LogOut, Building2, Mailbox, Folder, FolderPlus, X as XIcon, Plus, Tag as TagIcon, Sliders } from "lucide-react";

const FILTERS = [
  { id: "all", label: "All conversations", icon: Inbox },
  { id: "unread", label: "Unread", icon: Mail },
  { id: "sent", label: "Sent", icon: Send },
  { id: "assigned_to_me", label: "Assigned to me", icon: UserCheck },
  { id: "unassigned", label: "Unassigned", icon: UserX },
];

function StageDot({ color, active }) {
  return (
    <span
      className={`w-2 h-2 rounded-full shrink-0 ${active ? "ring-2 ring-offset-1 ring-zinc-900/20" : ""}`}
      style={{ background: color }}
    />
  );
}

function PipelineRow({ active, color, label, count, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
        active ? "bg-zinc-200/70 text-zinc-900 font-medium" : "text-zinc-600 hover:bg-zinc-100"
      }`}
      data-testid={testId}
    >
      <span className="flex items-center gap-2 truncate">
        <StageDot color={color} active={active} />
        <span className="truncate">{label}</span>
      </span>
      {count !== undefined && count !== null && (
        <span className="text-xs text-zinc-500 font-mono">{count}</span>
      )}
    </button>
  );
}

export default function Sidebar({
  filter, setFilter,
  statuses, status, setStatus,
  myTags, activeMyTag, setActiveMyTag,
  teamTags, activeTeamTag, setActiveTeamTag,
  customers, activeCustomer, setActiveCustomer,
  folders, activeFolder, setActiveFolder,
  onCreateFolder, onDeleteFolder,
  onCompose, onManageTags, onManagePipeline,
  onCloseDrawer,
  counts,
}) {
  const clearAll = () => {
    setActiveMyTag(null); setActiveTeamTag(null);
    setActiveCustomer(null); setActiveFolder(null);
  };
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const initials = (user?.name || "U").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
  const canAdmin = user && user.role === "owner";

  return (
    <aside className="w-60 shrink-0 bg-[#FAFAFB] border-r border-zinc-200 flex flex-col h-screen" data-testid="sidebar">
      {/* Brand */}
      <div className="px-4 h-14 flex items-center gap-2 border-b border-zinc-200">
        <div className="w-7 h-7 bg-[#0033CC] flex items-center justify-center rounded-sm">
          <Mailbox className="w-4 h-4 text-white" strokeWidth={2} />
        </div>
        <span className="font-heading text-base font-medium tracking-tight flex-1">Internal Inbox</span>
        {onCloseDrawer && (
          <button
            onClick={onCloseDrawer}
            className="md:hidden p-1.5 rounded-md text-zinc-500 hover:bg-zinc-100"
            data-testid="sidebar-drawer-close"
            aria-label="Close menu"
          >
            <XIcon className="w-4 h-4" strokeWidth={2} />
          </button>
        )}
      </div>

      {/* Compose */}
      <div className="px-3 pt-3">
        <button
          onClick={onCompose}
          className="w-full flex items-center justify-center gap-2 h-10 bg-[#0033CC] hover:bg-[#002299] text-white rounded-full text-sm font-medium shadow-sm transition-colors"
          data-testid="sidebar-compose-button"
        >
          <Plus className="w-4 h-4" strokeWidth={2.5} />
          Compose
        </button>
      </div>

      <div className="flex-1 overflow-y-auto py-3">
        <div className="px-3 mb-1">
          <p className="text-[10px] font-semibold tracking-[0.1em] uppercase text-zinc-500 px-2 mb-1.5">Inbox</p>
          {FILTERS.map((f) => {
            const Icon = f.icon;
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                onClick={() => { setFilter(f.id); setStatus(null); clearAll(); }}
                className={`w-full text-left flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                  active ? "bg-zinc-200/70 text-zinc-900 font-medium" : "text-zinc-600 hover:bg-zinc-100"
                }`}
                data-testid={`sidebar-filter-${f.id}`}
              >
                <span className="flex items-center gap-2 truncate">
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                  {f.label}
                </span>
                {counts?.[f.id] !== undefined && (
                  <span className="text-xs text-zinc-500 font-mono">{counts[f.id]}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Pipeline / Status */}
        <div className="px-3 mt-4 mb-1">
          <div className="flex items-center justify-between px-2 mb-1.5">
            <p className="text-[10px] font-semibold tracking-[0.1em] uppercase text-zinc-500">Pipeline</p>
            <button
              onClick={onManagePipeline}
              className="text-zinc-500 hover:text-[#0033CC] normal-case tracking-normal text-[10px] font-normal flex items-center gap-0.5"
              data-testid="sidebar-manage-pipeline"
              title={canAdmin ? "Manage pipeline stages" : "View pipeline stages"}
            >
              <Sliders className="w-2.5 h-2.5" strokeWidth={2} /> Manage
            </button>
          </div>
          {(statuses || []).map((s) => (
            <PipelineRow
              key={s.id}
              active={status === s.id}
              color={s.color}
              label={s.label}
              count={counts?.[`status_${s.id}`]}
              onClick={() => { setStatus(status === s.id ? null : s.id); setFilter("all"); clearAll(); }}
              testId={`sidebar-status-${s.id}`}
            />
          ))}
        </div>

        {/* Your tags (personal) — now styled as pipeline rows */}
        <div className="px-3 mt-4 mb-1">
          <div className="flex items-center justify-between px-2 mb-1.5">
            <p className="text-[10px] font-semibold tracking-[0.1em] uppercase text-zinc-500">Your tags</p>
            <button
              onClick={onManageTags}
              className="text-zinc-500 hover:text-[#0033CC] normal-case tracking-normal text-[10px] font-normal flex items-center gap-0.5"
              data-testid="sidebar-manage-tags"
              title="Manage all your tags"
            >
              <Sliders className="w-2.5 h-2.5" strokeWidth={2} /> Manage
            </button>
          </div>
          {(!myTags || myTags.length === 0) && (
            <p className="px-2 py-1.5 text-xs text-zinc-400 italic" data-testid="sidebar-mytags-empty">
              No personal tags yet.
            </p>
          )}
          {myTags?.map((t) => (
            <PipelineRow
              key={t.tag}
              active={activeMyTag === t.tag}
              color={t.color}
              label={t.tag}
              count={t.count}
              onClick={() => { setActiveMyTag(activeMyTag === t.tag ? null : t.tag); setFilter("all"); setStatus(null); setActiveTeamTag(null); setActiveCustomer(null); setActiveFolder(null); }}
              testId={`sidebar-mytag-${t.tag}`}
            />
          ))}
        </div>

        {/* Team tags */}
        <div className="px-3 mt-4 mb-1">
          <p className="text-[10px] font-semibold tracking-[0.1em] uppercase text-zinc-500 px-2 mb-1.5 flex items-center gap-1">
            <TagIcon className="w-3 h-3" strokeWidth={1.5} /> Team tags
          </p>
          {(!teamTags || teamTags.length === 0) && (
            <p className="px-2 py-1.5 text-xs text-zinc-400 italic" data-testid="sidebar-teamtags-empty">
              No team tags yet.
            </p>
          )}
          {teamTags?.map((t) => (
            <PipelineRow
              key={`tt-${t.tag}`}
              active={activeTeamTag === t.tag}
              color={t.color}
              label={t.tag}
              count={t.count}
              onClick={() => { setActiveTeamTag(activeTeamTag === t.tag ? null : t.tag); setFilter("all"); setStatus(null); setActiveMyTag(null); setActiveCustomer(null); setActiveFolder(null); }}
              testId={`sidebar-teamtag-${t.tag}`}
            />
          ))}
        </div>

        {/* Customers — auto-grouped by sender domain */}
        <div className="px-3 mt-4 mb-1">
          <p className="text-[10px] font-semibold tracking-[0.1em] uppercase text-zinc-500 px-2 mb-1.5 flex items-center gap-1">
            <Building2 className="w-3 h-3" strokeWidth={1.5} /> Customers
          </p>
          {(!customers || customers.length === 0) && (
            <p className="px-2 py-1.5 text-xs text-zinc-400 italic">No customers yet.</p>
          )}
          {customers?.slice(0, 30).map((c) => {
            const active = activeCustomer === c.domain;
            return (
              <button
                key={c.domain}
                onClick={() => { setActiveCustomer(active ? null : c.domain); setFilter("all"); setStatus(null); setActiveMyTag(null); setActiveTeamTag(null); setActiveFolder(null); }}
                className={`w-full text-left flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                  active ? "bg-amber-50 text-amber-700 font-medium" : "text-zinc-600 hover:bg-zinc-100"
                }`}
                data-testid={`sidebar-customer-${c.domain}`}
              >
                <span className="flex items-center gap-2 truncate">
                  <Building2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  <span className="truncate">{c.domain}</span>
                </span>
                <span className="text-xs text-zinc-500 font-mono">{c.thread_count}</span>
              </button>
            );
          })}
        </div>

        {/* Folders */}
        <div className="px-3 mt-4 mb-1">
          <div className="flex items-center justify-between px-2 mb-1.5">
            <p className="text-[10px] font-semibold tracking-[0.1em] uppercase text-zinc-500 flex items-center gap-1">
              <Folder className="w-3 h-3" strokeWidth={1.5} /> Folders
            </p>
            <button
              onClick={onCreateFolder}
              className="p-0.5 text-zinc-500 hover:text-[#0033CC] hover:bg-zinc-100 rounded transition-colors"
              title="New folder"
              data-testid="sidebar-create-folder"
            >
              <FolderPlus className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
          </div>
          {(!folders || folders.length === 0) && (
            <p className="px-2 py-1.5 text-xs text-zinc-400 italic">No folders yet.</p>
          )}
          {folders?.map((f) => {
            const active = activeFolder === f.id;
            return (
              <div key={f.id} className="group flex items-center">
                <button
                  onClick={() => { setActiveFolder(active ? null : f.id); setFilter("all"); setStatus(null); setActiveMyTag(null); setActiveTeamTag(null); setActiveCustomer(null); }}
                  className={`flex-1 text-left flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                    active ? "bg-zinc-200/70 text-zinc-900 font-medium" : "text-zinc-600 hover:bg-zinc-100"
                  }`}
                  data-testid={`sidebar-folder-${f.id}`}
                >
                  <span className="flex items-center gap-2 truncate">
                    <Folder className="w-3.5 h-3.5" strokeWidth={1.5} fill={active ? "currentColor" : "none"} />
                    <span className="truncate">{f.name}</span>
                  </span>
                  <span className="text-xs text-zinc-500 font-mono">{(f.thread_ids || []).length}</span>
                </button>
                <button
                  onClick={() => onDeleteFolder?.(f)}
                  className="opacity-0 group-hover:opacity-100 p-1 text-zinc-400 hover:text-red-600 transition-opacity"
                  title="Delete folder"
                  data-testid={`sidebar-delete-folder-${f.id}`}
                >
                  <XIcon className="w-3 h-3" strokeWidth={2} />
                </button>
              </div>
            );
          })}
        </div>


      </div>

      {/* User block */}
      <div className="border-t border-zinc-200 p-3">
        {canAdmin && (
          <button
            onClick={() => navigate("/admin")}
            className="w-full mb-2 flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-zinc-700 hover:bg-zinc-100 transition-colors"
            data-testid="sidebar-admin-button"
          >
            <Settings className="w-4 h-4" strokeWidth={1.5} /> Admin panel
          </button>
        )}
        <div className="flex items-center gap-2 px-2 py-1.5">
          <Avatar className="w-7 h-7">
            <AvatarFallback className="bg-[#0033CC] text-white text-xs font-medium">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-[10px] font-mono uppercase tracking-wide text-zinc-500">{user?.role}</p>
          </div>
          <button
            onClick={async () => { await logout(); navigate("/login"); }}
            className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded-md transition-colors"
            title="Sign out"
            data-testid="sidebar-logout-button"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </aside>
  );
}
